// Entity builders and visual helpers. Movement/game rules live in game.ts.

import * as THREE from 'three';
import { ROSTER, type CultMember } from './roster';
import type { BiomeId } from './world';
import type { AnimalSpecies } from './animals';

export function radialTexture(inner: string, outer: string, size = 128): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(size / 2, size / 2, size * 0.03, size / 2, size / 2, size / 2);
  grad.addColorStop(0, inner);
  grad.addColorStop(1, outer);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(c);
}

const shadowTex = /* lazy */ (() => {
  let tex: THREE.CanvasTexture | null = null;
  return () => (tex ??= radialTexture('rgba(0,0,0,0.42)', 'rgba(0,0,0,0)'));
})();

const glowTexOrange = (() => {
  let tex: THREE.CanvasTexture | null = null;
  return () => (tex ??= radialTexture('rgba(255,190,90,0.9)', 'rgba(247,147,26,0)'));
})();

const glowTexGreen = (() => {
  let tex: THREE.CanvasTexture | null = null;
  return () => (tex ??= radialTexture('rgba(180,255,140,0.9)', 'rgba(139,255,94,0)'));
})();

const glowTexRed = (() => {
  let tex: THREE.CanvasTexture | null = null;
  return () => (tex ??= radialTexture('rgba(220,40,30,0.95)', 'rgba(150,10,10,0)'));
})();

const glowTexBlue = (() => {
  let tex: THREE.CanvasTexture | null = null;
  return () => (tex ??= radialTexture('rgba(150,200,255,0.9)', 'rgba(80,130,220,0)'));
})();

const glowTexWhite = (() => {
  let tex: THREE.CanvasTexture | null = null;
  return () => (tex ??= radialTexture('rgba(255,255,255,0.9)', 'rgba(255,255,255,0)'));
})();

/** Tintable additive glow sprite (white texture — set material.color at runtime). */
export function makeGlowSprite(scale: number): THREE.Sprite {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexWhite(),
    transparent: true,
    opacity: 0.3,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  sprite.scale.set(scale, scale, 1);
  return sprite;
}

let bloodTex: THREE.CanvasTexture | null = null;

function getBloodTexture(): THREE.CanvasTexture {
  if (bloodTex) return bloodTex;
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d')!;
  // Irregular splat: overlapping blobs + droplets flung outward.
  g.fillStyle = 'rgba(150,16,12,0.95)';
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + Math.sin(i * 5.7) * 0.8;
    const d = 8 + ((i * 37) % 22);
    const r = 14 + ((i * 23) % 16);
    g.beginPath();
    g.arc(64 + Math.cos(a) * d, 64 + Math.sin(a) * d, r, 0, Math.PI * 2);
    g.fill();
  }
  g.fillStyle = 'rgba(120,10,8,0.9)';
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2 + Math.sin(i * 3.3) * 0.9;
    const d = 34 + ((i * 41) % 24);
    g.beginPath();
    g.arc(64 + Math.cos(a) * d, 64 + Math.sin(a) * d, 2.5 + ((i * 13) % 5), 0, Math.PI * 2);
    g.fill();
  }
  bloodTex = new THREE.CanvasTexture(c);
  return bloodTex;
}

export function makeBloodSplat(scale: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(scale * 2, scale * 2),
    new THREE.MeshBasicMaterial({ map: getBloodTexture(), transparent: true, opacity: 0.85, depthWrite: false }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.rotation.z = Math.random() * Math.PI * 2;
  mesh.renderOrder = 1;
  return mesh;
}

export function makeBlobShadow(radius: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 2, radius * 2),
    new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, depthWrite: false }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.renderOrder = 1;
  return mesh;
}

/* ---------------- saucer ---------------- */

export interface SaucerRig {
  group: THREE.Group;
  lights: THREE.Mesh[];
  lightMats: THREE.MeshBasicMaterial[];
  energyBands: THREE.InstancedMesh;
  energyBandMat: THREE.MeshBasicMaterial;
  shadow: THREE.Mesh;
  beam: BeamRig;
}

export interface BeamRig {
  group: THREE.Group;
  cone: THREE.Mesh;
  rings: THREE.Mesh[];
  decal: THREE.Mesh;
  light: THREE.PointLight;
}

function buildBeam(): BeamRig {
  const group = new THREE.Group();
  const height = 10;
  const cone = new THREE.Mesh(
    new THREE.CylinderGeometry(0.5, 2.9, height, 22, 1, true),
    new THREE.MeshBasicMaterial({
      color: '#8bff5e',
      transparent: true,
      opacity: 0.16,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  cone.position.y = -height / 2;
  group.add(cone);

  const rings: THREE.Mesh[] = [];
  const ringMat = new THREE.MeshBasicMaterial({
    color: '#b4ff8c',
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  for (let i = 0; i < 4; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1, 0.06, 6, 24), ringMat.clone());
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
    rings.push(ring);
  }

  const decal = new THREE.Mesh(
    new THREE.PlaneGeometry(6.4, 6.4),
    new THREE.MeshBasicMaterial({
      map: glowTexGreen(),
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  decal.rotation.x = -Math.PI / 2;
  decal.renderOrder = 2;
  group.add(decal);

  const light = new THREE.PointLight('#7dff5a', 26, 18, 1.6);
  light.position.y = -4;
  group.add(light);

  group.visible = false;
  return { group, cone, rings, decal, light };
}

export function buildSaucer(): SaucerRig {
  // Proper Roswell disc: wide flat brushed-metal saucer, rim lip, small grey
  // dome, porthole lights, three landing pods, antenna.
  const group = new THREE.Group();

  const hullMat = new THREE.MeshPhongMaterial({ color: '#c3ccd8', specular: '#ffffff', shininess: 90 });
  const profile: THREE.Vector2[] = [
    new THREE.Vector2(0.0, -0.5),
    new THREE.Vector2(0.55, -0.48),
    new THREE.Vector2(1.35, -0.34),
    new THREE.Vector2(2.3, -0.1),
    new THREE.Vector2(2.75, 0.0),
    new THREE.Vector2(2.62, 0.1),
    new THREE.Vector2(1.9, 0.26),
    new THREE.Vector2(1.15, 0.4),
    new THREE.Vector2(0.78, 0.44),
  ];
  const disc = new THREE.Mesh(new THREE.LatheGeometry(profile, 40), hullMat);
  group.add(disc);

  // Small metallic-glass dome, Roswell grey rather than cartoon blue.
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(0.72, 22, 12, 0, Math.PI * 2, 0, Math.PI / 2),
    new THREE.MeshPhongMaterial({ color: '#aebfd2', specular: '#ffffff', shininess: 160, transparent: true, opacity: 0.92 }),
  );
  dome.position.y = 0.42;
  group.add(dome);

  // Antenna.
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.03, 0.5, 5), hullMat);
  mast.position.y = 1.25;
  const tip = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 5), new THREE.MeshBasicMaterial({ color: '#ff5e5e' }));
  tip.position.y = 1.5;
  group.add(mast, tip);

  // Dark porthole ring on the upper slope.
  const portMat = new THREE.MeshPhongMaterial({ color: '#22282f', specular: '#8899aa', shininess: 40 });
  const portGeo = new THREE.CylinderGeometry(0.1, 0.1, 0.05, 8);
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const port = new THREE.Mesh(portGeo, portMat);
    port.position.set(Math.cos(a) * 1.5, 0.335, Math.sin(a) * 1.5);
    port.rotation.set(Math.sin(a) * 0.35, 0, -Math.cos(a) * 0.35);
    group.add(port);
  }

  // Chase lights inset along the rim edge.
  const lights: THREE.Mesh[] = [];
  const lightMats: THREE.MeshBasicMaterial[] = [];
  const lightGeo = new THREE.SphereGeometry(0.1, 8, 6);
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const mat = new THREE.MeshBasicMaterial({ color: '#f7931a' });
    const bulb = new THREE.Mesh(lightGeo, mat);
    bulb.position.set(Math.cos(a) * 2.68, 0.02, Math.sin(a) * 2.68);
    group.add(bulb);
    lights.push(bulb);
    lightMats.push(mat);
  }

  // Two continuous bands make the energy state readable even when perspective
  // hides half the individual chase lamps. Both rings share one material and
  // one instanced draw call; the lamps retain the moving chase on top.
  const energyBandMat = new THREE.MeshBasicMaterial({
    color: '#7dff5a', transparent: true, opacity: 0.72,
    blending: THREE.AdditiveBlending, depthWrite: false,
  });
  const energyBands = new THREE.InstancedMesh(
    new THREE.TorusGeometry(2.68, 0.055, 5, 48),
    energyBandMat,
    2,
  );
  energyBands.name = 'saucer-energy-rings';
  energyBands.renderOrder = 3;
  const band = new THREE.Object3D();
  for (let i = 0; i < 2; i++) {
    band.position.set(0, i === 0 ? -0.015 : 0.15, 0);
    band.rotation.set(Math.PI / 2, 0, 0);
    band.scale.setScalar(i === 0 ? 1.01 : 0.96);
    band.updateMatrix();
    energyBands.setMatrixAt(i, band.matrix);
  }
  energyBands.instanceMatrix.needsUpdate = true;
  group.add(energyBands);

  // Three hemispherical landing pods beneath.
  const podMat = new THREE.MeshPhongMaterial({ color: '#7c8698', specular: '#cdd8e8', shininess: 60 });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.5;
    const pod = new THREE.Mesh(new THREE.SphereGeometry(0.28, 10, 7), podMat);
    pod.position.set(Math.cos(a) * 1.3, -0.42, Math.sin(a) * 1.3);
    pod.scale.y = 0.75;
    group.add(pod);
  }

  const shadow = makeBlobShadow(2.6);
  const beam = buildBeam();
  group.add(beam.group);

  return { group, lights, lightMats, energyBands, energyBandMat, shadow, beam };
}

/* ---------------- shacks (some hide cult leaders) ---------------- */

const SHACK_WALLS = ['#6b4a2c', '#5c5244', '#74563a', '#4f5a66', '#6d4838', '#5a4a58'];
const SHACK_ROOFS = ['#3d3428', '#4a3020', '#38404c', '#443626', '#503a2a', '#3a3036'];

const MESA_WALLS = ['#b0713d', '#a06838', '#c08850', '#96582e'];
const SNOW_WALLS = ['#4a3624', '#54432f', '#3a2e20', '#5c4a36'];
const MOOR_WALLS = ['#7c7f78', '#6b6f68', '#8a8c82', '#5f6360'];
const TOWN_WALLS = ['#8a4c3c', '#7a4636', '#95584a', '#6d3f34'];
/** Off-world habitats: panelled shells, not painted timber. */
const DOME_WALLS = ['#b8bcc4', '#a2a6b0', '#c8ccd4', '#949aa6'];

/** Homestead per biome: valley shack, mesa adobe, snow log cabin, moor
 *  drystone bothy, town brick terrace-end. */
/**
 * @param drop How far the ground falls away under the footprint. The shack is
 * seated on the HIGHEST ground it covers, so it never sinks into the hill, and
 * this is the depth of footing needed to close the gap on the downhill side —
 * which is exactly what a footing is for.
 */
export function buildShack(variant: number, biome: BiomeId = 'valley', drop = 0): THREE.Group {
  const group = new THREE.Group();
  const wallColors = biome === 'mesa' ? MESA_WALLS
    : biome === 'snow' ? SNOW_WALLS
      : biome === 'moor' ? MOOR_WALLS
        : biome === 'town' ? TOWN_WALLS
          : biome === 'mars' || biome === 'moon' ? DOME_WALLS : SHACK_WALLS;
  const walls = new THREE.MeshLambertMaterial({ color: wallColors[variant % wallColors.length], flatShading: true });
  const roofMat = new THREE.MeshLambertMaterial({ color: SHACK_ROOFS[variant % SHACK_ROOFS.length], flatShading: true });
  const w = 2.6 + (variant % 3) * 0.5;
  const d = 2.1 + ((variant * 7) % 3) * 0.4;
  const h = 1.7 + (variant % 2) * 0.4;
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), walls);
  body.position.y = h / 2;
  group.add(body);
  if (drop > 0.05) {
    const footing = new THREE.Mesh(
      new THREE.BoxGeometry(w + 0.18, drop + 0.4, d + 0.18),
      new THREE.MeshLambertMaterial({ color: '#3d3a34', flatShading: true }),
    );
    footing.position.y = 0.1 - (drop + 0.4) / 2;
    group.add(footing);
  }
  if (biome === 'mesa') {
    // Adobe: flat roof slab with vigas poking through the parapet.
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(w + 0.24, 0.2, d + 0.24),
      new THREE.MeshLambertMaterial({ color: '#8a5a30', flatShading: true }),
    );
    slab.position.y = h + 0.1;
    group.add(slab);
    const vigaMat = new THREE.MeshLambertMaterial({ color: '#5c4126', flatShading: true });
    for (let i = 0; i < 3; i++) {
      const viga = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.55, 5), vigaMat);
      viga.rotation.x = Math.PI / 2;
      viga.position.set(-w / 3 + (i * w) / 3, h - 0.18, d / 2 + 0.14);
      group.add(viga);
    }
  } else if (biome === 'mars' || biome === 'moon') {
    // A habitat, not a house: a pressure dome over the module, an airlock on
    // the front, and a dish that is pointed at somewhere a long way off.
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(w * 0.66, 12, 7, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshLambertMaterial({ color: '#cfe0f0', transparent: true, opacity: 0.5, depthWrite: false }),
    );
    dome.position.y = h;
    const ribMat = new THREE.MeshLambertMaterial({ color: '#8e949e', flatShading: true });
    for (const turn of [0, Math.PI / 2]) {
      const rib = new THREE.Mesh(new THREE.TorusGeometry(w * 0.66, 0.05, 5, 16, Math.PI), ribMat);
      rib.position.y = h;
      rib.rotation.y = turn;
      group.add(rib);
    }
    const lock = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.9, 9), ribMat);
    lock.rotation.x = Math.PI / 2;
    lock.position.set(0, 0.55, d / 2 + 0.3);
    const dish = new THREE.Mesh(
      new THREE.SphereGeometry(0.42, 10, 6, 0, Math.PI * 2, 0, Math.PI / 3),
      new THREE.MeshLambertMaterial({ color: '#d4d8e0', flatShading: true, side: THREE.DoubleSide }),
    );
    dish.position.set(-w * 0.34, h + 0.85, -d * 0.2);
    dish.rotation.set(-0.9, 0, 0.5);
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.9, 5), ribMat);
    mast.position.set(-w * 0.34, h + 0.45, -d * 0.2);
    group.add(dome, lock, dish, mast);
  } else {
    const steep = biome === 'snow';
    const slate = biome === 'moor' ? '#5c6164' : biome === 'town' ? '#5a6270' : null;
    const roof = new THREE.Mesh(
      new THREE.CylinderGeometry(0.01, w * 0.72, steep ? 1.5 : 1.1, 4, 1),
      steep ? new THREE.MeshLambertMaterial({ color: '#2c2620', flatShading: true })
        : slate ? new THREE.MeshLambertMaterial({ color: slate, flatShading: true }) : roofMat,
    );
    roof.rotation.y = Math.PI / 4;
    roof.scale.z = d / w * 0.94;
    roof.position.y = h + (steep ? 0.75 : 0.55);
    group.add(roof);
    if (biome === 'moor') {
      // A lean-to against the gable, for the dog and the peat.
      const lean = new THREE.Mesh(new THREE.BoxGeometry(0.9, h * 0.62, d * 0.7), walls);
      lean.position.set(-w / 2 - 0.4, h * 0.31, 0);
      const leanRoof = new THREE.Mesh(
        new THREE.BoxGeometry(1.1, 0.12, d * 0.78),
        new THREE.MeshLambertMaterial({ color: '#5c6164', flatShading: true }),
      );
      leanRoof.position.set(-w / 2 - 0.42, h * 0.66, 0);
      leanRoof.rotation.z = 0.16;
      group.add(lean, leanRoof);
    }
    if (biome === 'town') {
      // Chimney pots — the skyline of every street in the country.
      const potMat = new THREE.MeshLambertMaterial({ color: '#c56a4a', flatShading: true });
      for (const dz of [-0.18, 0.18]) {
        const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.09, 0.3, 7), potMat);
        pot.position.set(w * 0.28, h + 1.2, -d * 0.2 + dz);
        group.add(pot);
      }
    }
    if (steep) {
      // Snow cap sitting on the ridge.
      const cap = new THREE.Mesh(
        new THREE.CylinderGeometry(0.01, w * 0.48, 0.55, 4, 1),
        new THREE.MeshLambertMaterial({ color: '#e6eef6', flatShading: true }),
      );
      cap.rotation.y = Math.PI / 4;
      cap.scale.z = d / w * 0.94;
      cap.position.y = h + 1.28;
      group.add(cap);
    }
  }
  const door = new THREE.Mesh(
    new THREE.PlaneGeometry(0.6, 1.1),
    new THREE.MeshBasicMaterial({ color: '#1c1410' }),
  );
  door.position.set(0, 0.55, d / 2 + 0.01);
  group.add(door);
  const win = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.5),
    new THREE.MeshBasicMaterial({ color: '#ffd98a' }),
  );
  win.position.set(w / 2 + 0.01, 1.0, 0);
  win.rotation.y = Math.PI / 2;
  group.add(win);
  if (biome === 'town') {
    // Sash pair either side of the door: a lit front room and a dark one.
    for (const [wx, lit] of [[-w * 0.26, true], [w * 0.26, false]] as const) {
      const sash = new THREE.Mesh(
        new THREE.PlaneGeometry(0.42, 0.55),
        new THREE.MeshBasicMaterial({ color: lit ? '#ffd98a' : '#1b2230' }),
      );
      sash.position.set(wx, 1.28, d / 2 + 0.01);
      group.add(sash);
    }
  }
  if (biome !== 'mars' && biome !== 'moon') {
    const chimney = new THREE.Mesh(
      new THREE.BoxGeometry(0.28, 0.8, 0.28),
      biome === 'snow' ? new THREE.MeshLambertMaterial({ color: '#8494b4', flatShading: true }) : walls,
    );
    chimney.position.set(w * 0.28, h + 0.7, -d * 0.2);
    group.add(chimney); // nothing to vent in a sealed habitat
  }
  // A breakable homestead shakes and disappears as one object; none of its
  // trim articulates. Preserve every material while collapsing its static
  // pieces so nine buildings do not each submit a small scene graph.
  return bakeProto(group);
}

/* ---------------- the grid: pylons and live spans ---------------- */
// The first thing in the game the world can hurt you with. A tower is solid at
// any altitude; the conductors hang in the saucer's cruise band and sag into it
// mid-span, so a transmission line is a wall you route around — or beam down.

export const PYLON_HEIGHT = 15.2;
/** Height above a tower's base where the lowest conductors leave the arm.
 *  Picked against SAUCER_ALT (7.4) so a sagging span crosses the hull. */
export const CONDUCTOR_H = 8.7;
/** Second circuit, on the upper arm. */
export const CONDUCTOR_H2 = 11.6;
/** Mid-span droop, in world units. */
export const WIRE_SAG = 1.9;
/** Half-width of a tower's solid core in XZ. Fly inside it and you have hit it. */
export const PYLON_RADIUS = 2.3;
/** Lateral conductor offsets from the span centreline, lower then upper arm. */
const CONDUCTOR_OFFSETS: [number, number][] = [
  [-3.3, CONDUCTOR_H], [3.3, CONDUCTOR_H],
  [-2.4, CONDUCTOR_H2], [2.4, CONDUCTOR_H2],
  [0, PYLON_HEIGHT - 0.6], // earth wire along the peaks
];

export interface PylonRig {
  group: THREE.Group;
  /** Per-tower so the warning lamps blink out of phase; disposed with the run. */
  lampMat: THREE.MeshBasicMaterial;
  glow: THREE.Sprite;
}

/** A lattice member between two local points. */
function strut(
  group: THREE.Group,
  from: THREE.Vector3,
  to: THREE.Vector3,
  radius: number,
  material: THREE.Material,
): void {
  const dir = to.clone().sub(from);
  const member = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, dir.length(), 4), material);
  member.position.copy(from).add(to).multiplyScalar(0.5);
  member.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  group.add(member);
}

let pylonProto: THREE.Group | null = null;

function buildPylonProto(): THREE.Group {
  const group = new THREE.Group();
  const steel = new THREE.MeshLambertMaterial({ color: '#6d757f', flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: '#4a505a', flatShading: true });

  // Four splayed legs, kinked at the waist where the body narrows.
  const foot = 1.75;
  const waist = 0.62;
  const shoulder = 0.42;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      strut(group,
        new THREE.Vector3(sx * foot, 0, sz * foot),
        new THREE.Vector3(sx * waist, CONDUCTOR_H, sz * waist), 0.1, steel);
      strut(group,
        new THREE.Vector3(sx * waist, CONDUCTOR_H, sz * waist),
        new THREE.Vector3(sx * shoulder, PYLON_HEIGHT, sz * shoulder), 0.08, steel);
    }
  }
  // One horizontal band at the waist, so it reads as lattice not as legs.
  for (const [bx, bz, bw, bd] of [
    [0, waist, waist * 2, 0.1], [0, -waist, waist * 2, 0.1],
    [waist, 0, 0.1, waist * 2], [-waist, 0, 0.1, waist * 2],
  ] as const) {
    const band = new THREE.Mesh(new THREE.BoxGeometry(bw, 0.1, bd), dark);
    band.position.set(bx, CONDUCTOR_H - 0.35, bz);
    group.add(band);
  }
  // Cross-arms carrying the two circuits.
  for (const [ay, aw] of [[CONDUCTOR_H, 7.2], [CONDUCTOR_H2, 5.4]] as const) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(aw, 0.22, 0.26), steel);
    arm.position.y = ay + 0.5;
    group.add(arm);
    for (const side of [-1, 1]) {
      const insulator = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.5, 6), dark);
      insulator.position.set((side * aw) / 2 - side * 0.3, ay + 0.24, 0);
      group.add(insulator);
    }
  }
  // Peak and the aircraft warning lamp.
  const peak = new THREE.Mesh(new THREE.BoxGeometry(shoulder * 2.2, 0.18, shoulder * 2.2), steel);
  peak.position.y = PYLON_HEIGHT;
  group.add(peak);
  // The lattice only ever moves as one tower. Collapse its nineteen static
  // members to one mesh per material so Town does not spend most of its phone
  // render budget submitting individual struts.
  return bakeProto(group);
}

export function buildPylon(): PylonRig {
  pylonProto ??= buildPylonProto();
  const group = pylonProto.clone();
  const lampMat = new THREE.MeshBasicMaterial({ color: '#ff3030' });
  const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), lampMat);
  lamp.position.y = PYLON_HEIGHT + 0.3;
  group.add(lamp);
  // The lamp is the tower's signature from the far side of the map, where the
  // steel itself is a grey smudge against grey ground. Worth being generous.
  const glow = makeGlowSprite(3.2);
  glow.material.color.set('#ff4030');
  glow.material.opacity = 0.45;
  glow.position.y = PYLON_HEIGHT + 0.3;
  group.add(glow);
  return { group, lampMat, glow };
}

let stumpProto: THREE.Group | null = null;

/**
 * What is left where a tower came down: four cut legs, the lower cross-arm
 * lying in the grass, and a coil of dead conductor. It explains why a line
 * stops, and it is the landmark that says "the way through is here" — a gap in
 * a line is invisible from the air, a wreck is not.
 */
export function buildPylonStump(): THREE.Group {
  if (!stumpProto) {
    const group = new THREE.Group();
    const steel = new THREE.MeshLambertMaterial({ color: '#5c646e', flatShading: true });
    const rust = new THREE.MeshLambertMaterial({ color: '#6b4a34', flatShading: true });
    const foot = 1.75;
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        // Cut off at knee height, each leg sheared at its own angle.
        const cut = 1.6 + ((sx + sz + 2) % 3) * 0.5;
        strut(group,
          new THREE.Vector3(sx * foot, 0, sz * foot),
          new THREE.Vector3(sx * foot * 0.72, cut, sz * foot * 0.72), 0.1, steel);
      }
    }
    const arm = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.22, 0.26), steel);
    arm.position.set(1.4, 0.2, 2.6);
    arm.rotation.y = 0.42;
    arm.rotation.z = 0.06;
    group.add(arm);
    const coil = new THREE.Mesh(new THREE.TorusGeometry(1.15, 0.09, 5, 12), rust);
    coil.rotation.x = Math.PI / 2 - 0.2;
    coil.position.set(-2.4, 0.28, -1.6);
    group.add(coil);
    stumpProto = bakeProto(group);
  }
  return stumpProto.clone();
}

/** The clone shares the prototype's geometry and steel, so only the two
 *  per-tower materials are ours to release. */
export function disposePylon(rig: PylonRig): void {
  rig.lampMat.dispose();
  rig.glow.material.dispose();
}

/** Parabolic sag along a span; `t` runs 0..1 from the first tower to the second. */
export function wireYAt(fromY: number, toY: number, t: number): number {
  return fromY + (toY - fromY) * t - WIRE_SAG * 4 * t * (1 - t);
}

export interface SpanRig {
  group: THREE.Group;
  wires: THREE.Mesh;
  markers: THREE.InstancedMesh;
}

/**
 * One span: each conductor is a narrow ribbon facing the sky, which is what
 * actually reads under a top-down camera (a 1px line does not), plus the orange
 * marker spheres a real line wears where it crosses somewhere aircraft go.
 * The markers ride the LOWEST conductors — the ones at hull height — because
 * their job here is not authenticity, it is telling a pilot which altitude
 * band is about to kill them. Built in world space: the endpoints are the two
 * tower bases.
 */
export function buildWireSpan(from: THREE.Vector3, to: THREE.Vector3): SpanRig {
  const segments = 14;
  const halfW = 0.085;
  const along = to.clone().sub(from).setY(0);
  const perp = new THREE.Vector3(-along.z, 0, along.x).normalize();
  const positions: number[] = [];
  const indices: number[] = [];
  for (const [lateral, height] of CONDUCTOR_OFFSETS) {
    const base = positions.length / 3;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const x = from.x + (to.x - from.x) * t + perp.x * lateral;
      const z = from.z + (to.z - from.z) * t + perp.z * lateral;
      const y = wireYAt(from.y + height, to.y + height, t);
      positions.push(x - perp.x * halfW, y, z - perp.z * halfW);
      positions.push(x + perp.x * halfW, y, z + perp.z * halfW);
    }
    for (let i = 0; i < segments; i++) {
      const a = base + i * 2;
      indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  // Unlit but subdued: the whole grid can be read against dark ground without
  // distant conductors bleaching the town. Nearby markers carry the warning.
  const wires = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: '#667286', side: THREE.DoubleSide }));

  const spots: [number, number][] = []; // t along the span, lateral offset
  for (const t of [0.28, 0.5, 0.72]) for (const lateral of [-3.3, 3.3]) spots.push([t, lateral]);
  const markers = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.42, 8, 6),
    new THREE.MeshLambertMaterial({ color: '#d85f28', emissive: '#7c2d0d', emissiveIntensity: 0.3, flatShading: true }),
    spots.length,
  );
  markers.name = 'wire-markers';
  const m = new THREE.Matrix4();
  for (let i = 0; i < spots.length; i++) {
    const [t, lateral] = spots[i];
    m.setPosition(
      from.x + (to.x - from.x) * t + perp.x * lateral,
      wireYAt(from.y + CONDUCTOR_H, to.y + CONDUCTOR_H, t),
      from.z + (to.z - from.z) * t + perp.z * lateral,
    );
    markers.setMatrixAt(i, m);
  }
  markers.instanceMatrix.needsUpdate = true;

  const group = new THREE.Group();
  group.add(wires, markers);
  return { group, wires, markers };
}

/** Both of a span's geometries and materials; nothing here is shared. */
export function disposeWireSpan(rig: SpanRig): void {
  rig.wires.geometry.dispose();
  (rig.wires.material as THREE.Material).dispose();
  rig.markers.geometry.dispose();
  (rig.markers.material as THREE.Material).dispose();
  rig.markers.dispose();
}

/** The flash where hull meets conductor: a hot core and a fan of sparks. */
export function buildArcFlash(): THREE.Group {
  const group = new THREE.Group();
  const core = makeGlowSprite(4.4);
  core.material.color.set('#dff0ff');
  core.material.opacity = 0.9;
  group.add(core);
  const points: THREE.Vector3[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 + Math.sin(i * 3.1) * 0.4;
    const r = 1.1 + (i % 3) * 0.5;
    points.push(new THREE.Vector3(0, 0, 0));
    points.push(new THREE.Vector3(Math.cos(a) * r, (i % 2 ? 0.5 : -0.5) * 0.7, Math.sin(a) * r));
  }
  const bolts = new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.LineBasicMaterial({
      color: '#bfe8ff',
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  group.add(bolts);
  return group;
}

/* ---------------- hidey props (each hides a cult leader) ---------------- */

export interface HideyRig {
  group: THREE.Group;
  aura: THREE.Sprite;
  palette: string[]; // chunk colours when it pops
  label: string;
}

function lam(color: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color, flatShading: true });
}

/** Something in the landscape is not quite right: it wiggles, it glows
 *  faintly, and it explodes under the beam to reveal a cult leader.
 *  Three per biome — a gentle find-the-leader puzzle, not a hard one. */
export function buildHideyProp(biome: BiomeId, variant: number): HideyRig {
  const group = new THREE.Group();
  let palette: string[];
  let label: string;
  const v = variant % 3;
  if ((biome === 'valley' || biome === 'mesa') && v === 0) {
    // The hero pumpkin in a real patch: visibly too large, faintly alive and
    // carrying somebody who should not fit inside it.
    label = 'POSSESSED PUMPKIN';
    palette = ['#e87c1e', '#f7931a', '#4a6030'];
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.92, 12, 8), lam('#e87c1e'));
    body.position.y = 0.72;
    body.scale.y = 0.78;
    const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.14, 0.48, 6), lam('#4a6030'));
    stalk.position.y = 1.47;
    const face = lam('#2a160c');
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.05, 3), face);
      eye.position.set(side * 0.28, 0.84, 0.88);
      eye.rotation.x = Math.PI / 2;
      group.add(eye);
    }
    const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.09, 0.06), face);
    mouth.position.set(0, 0.54, 0.91);
    group.add(body, stalk, mouth);
  } else if (biome === 'mesa') {
    if (v === 1) {
      // A covered wagon nobody drove here.
      label = 'ABANDONED WAGON';
      palette = ['#6b4a2c', '#d8cfb8', '#54432f'];
      const bed = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.5, 1.3), lam('#6b4a2c'));
      bed.position.y = 0.85;
      const canvas = new THREE.Mesh(
        new THREE.CylinderGeometry(0.75, 0.75, 2.2, 10, 1, false, 0, Math.PI),
        lam('#d8cfb8'),
      );
      canvas.rotation.z = Math.PI / 2;
      canvas.position.y = 1.35;
      group.add(bed, canvas);
      const wheelGeo = new THREE.CylinderGeometry(0.4, 0.4, 0.1, 10);
      for (const [wx, wz] of [[-0.85, 0.7], [0.85, 0.7], [-0.85, -0.7], [0.85, -0.7]]) {
        const wheel = new THREE.Mesh(wheelGeo, lam('#3a2e20'));
        wheel.rotation.x = Math.PI / 2;
        wheel.position.set(wx, 0.4, wz);
        group.add(wheel);
      }
    } else {
      // Barrels stacked with intent.
      label = 'DODGY BARRELS';
      palette = ['#7a4a2c', '#5c4126', '#8a5a30'];
      const barrelGeo = new THREE.CylinderGeometry(0.42, 0.42, 0.95, 9);
      const wood = lam('#7a4a2c');
      for (const [bx, bz, by] of [[-0.45, 0, 0.48], [0.45, 0.1, 0.48], [0, 0.05, 1.4]]) {
        const barrel = new THREE.Mesh(barrelGeo, wood);
        barrel.position.set(bx, by, bz);
        group.add(barrel);
      }
    }
  } else if (biome === 'moor') {
    if (v === 0) {
      // A cairn nobody remembers stacking, on a path nobody walks.
      label = 'SUSPICIOUS CAIRN';
      palette = ['#8a8c82', '#6b6f68', '#5f6360'];
      const stone = lam('#7c7f78');
      const sizes = [0.62, 0.5, 0.4, 0.3, 0.22];
      let y = 0;
      sizes.forEach((r, i) => {
        const slab = new THREE.Mesh(new THREE.DodecahedronGeometry(r, 0), stone);
        slab.position.set(Math.sin(i * 2.3) * 0.09, y + r * 0.7, Math.cos(i * 1.7) * 0.09);
        slab.rotation.set(i * 0.7, i * 1.3, i * 0.4);
        slab.scale.y = 0.72;
        group.add(slab);
        y += r * 1.15;
      });
    } else if (v === 1) {
      // Gorse. Yellow, spiny, and breathing.
      label = 'TWITCHY GORSE';
      palette = ['#4c5c2e', '#3a4824', '#d8c24a'];
      const bush = lam('#4c5c2e');
      for (const [bx, by, bz, r] of [[0, 0.55, 0, 0.7], [0.42, 0.42, 0.2, 0.5], [-0.36, 0.46, -0.24, 0.54]]) {
        const lump = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), bush);
        lump.position.set(bx, by, bz);
        group.add(lump);
      }
      const flower = lam('#d8c24a');
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const bud = new THREE.Mesh(new THREE.SphereGeometry(0.09, 5, 4), flower);
        bud.position.set(Math.cos(a) * 0.58, 0.72 + Math.sin(i * 2.1) * 0.2, Math.sin(a) * 0.5);
        group.add(bud);
      }
    } else {
      // A cast-iron bath used as a sheep trough. Something is in the bath.
      label = 'OCCUPIED BATH TROUGH';
      palette = ['#b8bcc2', '#8a8c82', '#3f4a52'];
      const enamel = lam('#b8bcc2');
      const tub = new THREE.Mesh(
        new THREE.CylinderGeometry(0.62, 0.55, 1.9, 10, 1, false, 0, Math.PI),
        enamel,
      );
      tub.rotation.z = Math.PI / 2;
      tub.rotation.y = Math.PI;
      tub.position.y = 0.62;
      const water = new THREE.Mesh(
        new THREE.PlaneGeometry(1.85, 1.05),
        new THREE.MeshLambertMaterial({ color: '#3f4a52', flatShading: true }),
      );
      water.rotation.x = -Math.PI / 2;
      water.position.y = 0.6;
      group.add(tub, water);
      const footGeo = new THREE.CylinderGeometry(0.08, 0.06, 0.22, 6);
      for (const [fx, fz] of [[-0.72, 0.3], [0.72, 0.3], [-0.72, -0.3], [0.72, -0.3]]) {
        const foot = new THREE.Mesh(footGeo, lam('#8a8c82'));
        foot.position.set(fx, 0.11, fz);
        group.add(foot);
      }
    }
  } else if (biome === 'town') {
    if (v === 0) {
      // Collection day. This one is rattling.
      label = 'RATTLING WHEELIE BIN';
      palette = ['#2f4a3a', '#24382c', '#1c1f24'];
      const shell = lam('#2f4a3a');
      const bin = new THREE.Mesh(new THREE.BoxGeometry(1.0, 1.4, 0.86), shell);
      bin.position.y = 0.78;
      const lid = new THREE.Mesh(new THREE.BoxGeometry(1.06, 0.12, 0.92), lam('#24382c'));
      lid.position.set(0, 1.52, 0.04);
      lid.rotation.x = -0.12;
      group.add(bin, lid);
      const wheelGeo = new THREE.CylinderGeometry(0.16, 0.16, 0.1, 9);
      for (const side of [-1, 1]) {
        const wheel = new THREE.Mesh(wheelGeo, lam('#1c1f24'));
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(side * 0.42, 0.16, -0.3);
        group.add(wheel);
      }
    } else if (v === 1) {
      // The line has been dead for thirty years. Someone is still in there.
      label = 'OCCUPIED PHONE BOX';
      palette = ['#a01f1f', '#7c1717', '#e8e4da'];
      const red = lam('#a01f1f');
      const box = new THREE.Mesh(new THREE.BoxGeometry(1.05, 2.5, 1.05), red);
      box.position.y = 1.25;
      const crown = new THREE.Mesh(new THREE.BoxGeometry(1.16, 0.3, 1.16), lam('#7c1717'));
      crown.position.y = 2.62;
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.14, 7, 5), lam('#7c1717'));
      cap.position.y = 2.84;
      group.add(box, crown, cap);
      // Glazing on all four faces, one of them silhouetted.
      const glass = new THREE.MeshLambertMaterial({ color: '#26323c', flatShading: true });
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        const pane = new THREE.Mesh(new THREE.PlaneGeometry(0.72, 1.5), glass);
        pane.position.set(Math.sin(a) * 0.53, 1.55, Math.cos(a) * 0.53);
        pane.rotation.y = a;
        group.add(pane);
      }
      const figure = new THREE.Mesh(new THREE.BoxGeometry(0.42, 1.3, 0.3), lam('#1a1f28'));
      figure.position.y = 1.35;
      group.add(figure);
    } else {
      // A skip that arrived without a permit and fills up on its own.
      label = 'DODGY SKIP';
      palette = ['#c2761e', '#8a5214', '#54432f'];
      const steel = lam('#c2761e');
      const floor = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.16, 1.3), steel);
      floor.position.y = 0.3;
      group.add(floor);
      for (const [sx, sz, sw, sd, rot] of [
        [-1.2, 0, 0.12, 1.3, 0], [1.2, 0, 0.12, 1.3, 0],
        [0, -0.65, 2.4, 0.12, 0], [0, 0.65, 2.4, 0.12, 0],
      ] as const) {
        const side = new THREE.Mesh(new THREE.BoxGeometry(sw, 0.85, sd), steel);
        side.position.set(sx, 0.72, sz);
        side.rotation.y = rot;
        group.add(side);
      }
      const spoil = lam('#54432f');
      for (let i = 0; i < 5; i++) {
        const rubble = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.22, 0.3), spoil);
        rubble.position.set(-0.9 + i * 0.45, 0.95 + (i % 2) * 0.12, Math.sin(i * 2.2) * 0.3);
        rubble.rotation.set(i * 0.3, i * 0.9, i * 0.2);
        group.add(rubble);
      }
    }
  } else if (biome === 'snow') {
    if (v === 0) {
      // The classic. Nobody built him. He was simply there.
      label = 'UNEXPLAINED SNOWMAN';
      palette = ['#e6eef6', '#cfdee2', '#f7931a'];
      const snow = lam('#e6eef6');
      const base = new THREE.Mesh(new THREE.SphereGeometry(0.85, 10, 8), snow);
      base.position.y = 0.7;
      const mid = new THREE.Mesh(new THREE.SphereGeometry(0.6, 10, 8), snow);
      mid.position.y = 1.75;
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.4, 10, 8), snow);
      head.position.y = 2.55;
      const carrot = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.5, 6), lam('#f7931a'));
      carrot.rotation.x = Math.PI / 2;
      carrot.position.set(0, 2.55, 0.55);
      group.add(base, mid, head, carrot);
      const coal = lam('#14161c');
      for (const dx of [-0.14, 0.14]) {
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), coal);
        eye.position.set(dx, 2.68, 0.34);
        group.add(eye);
      }
    } else if (v === 1) {
      // A block of lake ice with a person-shaped shadow inside.
      label = 'OMINOUS ICE BLOCK';
      palette = ['#9cbcd6', '#cfe0ff', '#5a728c'];
      const ice = new THREE.Mesh(
        new THREE.BoxGeometry(1.5, 2.2, 1.1),
        new THREE.MeshPhongMaterial({
          color: '#9cbcd6', transparent: true, opacity: 0.55,
          specular: '#ffffff', shininess: 120, flatShading: true,
        }),
      );
      ice.position.y = 1.1;
      const shadow = new THREE.Mesh(new THREE.BoxGeometry(0.6, 1.6, 0.4), lam('#2c3a4a'));
      shadow.position.y = 1.0;
      group.add(shadow, ice);
    } else {
      // Firewood, stacked by no known hand.
      label = 'SUSPICIOUS LOG PILE';
      palette = ['#54432f', '#4a3624', '#6b4a2c'];
      const logGeo = new THREE.CylinderGeometry(0.28, 0.28, 2.0, 8);
      const wood = lam('#54432f');
      for (const [ly, lz] of [[0.3, -0.3], [0.3, 0.3], [0.82, 0]]) {
        const log = new THREE.Mesh(logGeo, wood);
        log.rotation.z = Math.PI / 2;
        log.position.set(0, ly, lz);
        group.add(log);
      }
    }
  } else if (biome === 'mars' || biome === 'moon') {
    // Nothing grows up here, so what hides a cult leader is other people's
    // hardware: the things the agencies left lying about.
    if (v === 0) {
      label = 'SILENT LANDER';
      palette = ['#c8ccd4', '#8e949e', '#f7c04a'];
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.78, 0.92, 0.62, 8), lam('#c8ccd4'));
      body.position.y = 0.86;
      const foil = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.94, 0.2, 8), lam('#f7c04a'));
      foil.position.y = 0.52;
      group.add(body, foil);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.7;
        const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.05, 5), lam('#8e949e'));
        leg.position.set(Math.cos(a) * 0.62, 0.42, Math.sin(a) * 0.62);
        leg.rotation.z = -Math.cos(a) * 0.5;
        leg.rotation.x = Math.sin(a) * 0.5;
        const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.07, 7), lam('#8e949e'));
        foot.position.set(Math.cos(a) * 0.9, 0.04, Math.sin(a) * 0.9);
        group.add(leg, foot);
      }
    } else if (v === 1) {
      label = 'ROVER, UNATTENDED';
      palette = ['#9aa0aa', '#3a3f48', '#d8dce4'];
      const deck = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.34, 0.98), lam('#9aa0aa'));
      deck.position.y = 0.72;
      const panel = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.06, 0.86), lam('#3a3f48'));
      panel.position.y = 0.92;
      const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.72, 5), lam('#d8dce4'));
      mast.position.set(0.5, 1.2, 0);
      const head = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.16, 0.34), lam('#d8dce4'));
      head.position.set(0.5, 1.58, 0);
      group.add(deck, panel, mast, head);
      const wheelGeo = new THREE.CylinderGeometry(0.26, 0.26, 0.16, 9);
      for (const [wx, wz] of [[-0.52, -0.5], [-0.52, 0.5], [0.52, -0.5], [0.52, 0.5]]) {
        const wheel = new THREE.Mesh(wheelGeo, lam('#3a3f48'));
        wheel.rotation.x = Math.PI / 2;
        wheel.position.set(wx, 0.28, wz);
        group.add(wheel);
      }
    } else {
      label = 'FLAG, PLANTED';
      palette = ['#d8dce4', '#c0392b', '#8e949e'];
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.05, 2.1, 5), lam('#d8dce4'));
      pole.position.y = 1.05;
      // Wired stiff, because there is no wind to fly it.
      const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.86, 0.54), new THREE.MeshLambertMaterial({
        color: '#c0392b', flatShading: true, side: THREE.DoubleSide,
      }));
      flag.position.set(0.45, 1.78, 0);
      const cairn = new THREE.Mesh(new THREE.DodecahedronGeometry(0.44, 0), lam('#8e949e'));
      cairn.position.set(-0.3, 0.24, 0.2);
      cairn.scale.y = 0.62;
      group.add(pole, flag, cairn);
    }
  } else {
    if (v === 1) {
      // A hay bale, faintly breathing.
      label = 'BREATHING HAY BALE';
      palette = ['#b3a04a', '#8c7a2c', '#d8cfb8'];
      const bale = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.75, 1.5, 12), lam('#b3a04a'));
      bale.rotation.z = Math.PI / 2;
      bale.position.y = 0.75;
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.76, 0.05, 6, 16), lam('#8c7a2c'));
      band.rotation.y = Math.PI / 2;
      band.position.y = 0.75;
      group.add(bale, band);
    } else {
      // An outhouse. Occupied.
      label = 'OCCUPIED OUTHOUSE';
      palette = ['#54432f', '#3a2e20', '#6b4a2c'];
      const booth = new THREE.Mesh(new THREE.BoxGeometry(1.1, 2.2, 1.1), lam('#54432f'));
      booth.position.y = 1.1;
      const roof = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.14, 1.3), lam('#3a2e20'));
      roof.position.y = 2.28;
      roof.rotation.z = 0.08;
      const moon = new THREE.Mesh(
        new THREE.CircleGeometry(0.12, 10),
        new THREE.MeshBasicMaterial({ color: '#ffd98a' }),
      );
      moon.position.set(0, 1.7, 0.56);
      group.add(booth, roof, moon);
    }
  }
  // The tell: a soft cult-orange aura, pulsed by the game.
  const aura = makeGlowSprite(3.6);
  aura.material.color.set('#f7931a');
  aura.material.opacity = 0.16;
  aura.position.y = 1.2;
  group.add(aura);
  return { group, aura, palette, label };
}

/* ---------------- normies ---------------- */

export interface NormieRig {
  group: THREE.Group;
  gun: THREE.Mesh;
  torsoMat: THREE.MeshLambertMaterial;
  warning: THREE.Sprite;
}

let threatWarningTex: THREE.CanvasTexture | null = null;

/** Camera-facing danger mark used only while an armed enemy is locking a shot. */
export function makeThreatWarning(): THREE.Sprite {
  if (!threatWarningTex) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    ctx.shadowColor = '#ff301f';
    ctx.shadowBlur = 18;
    ctx.fillStyle = 'rgba(8, 9, 13, 0.9)';
    ctx.beginPath();
    ctx.arc(64, 64, 45, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 9;
    ctx.strokeStyle = '#ff5e4a';
    ctx.stroke();
    ctx.shadowBlur = 10;
    ctx.fillStyle = '#fff2d5';
    ctx.font = '900 76px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', 64, 63);
    threatWarningTex = new THREE.CanvasTexture(canvas);
    threatWarningTex.colorSpace = THREE.SRGBColorSpace;
  }
  const warning = new THREE.Sprite(new THREE.SpriteMaterial({
    map: threatWarningTex,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false,
  }));
  warning.visible = false;
  warning.renderOrder = 8;
  warning.scale.setScalar(2.2);
  return warning;
}

const NORMIE_SHIRTS = ['#8c3a2c', '#31518c', '#3f6b35', '#6b3a7d', '#8c7a2c'];
const MESA_SHIRTS = ['#8a6a3c', '#6b3a2c', '#a05a2c', '#54432f', '#8c7a2c'];
const SNOW_PARKAS = ['#31518c', '#3f6b35', '#7d3a3a', '#4a3a6b', '#3a5a6b'];
/** EVA suits. Grubby off-white, because everything up there is. */
const SUIT_WHITES = ['#d8d4cc', '#c4c8d0', '#e0dcd2', '#b8bcc6'];
const MOOR_TWEEDS = ['#5c5a3c', '#6b5f42', '#4a4c34', '#7a6a48', '#3f4a34'];
const TOWN_HIVIS = ['#d8e03a', '#e8c832', '#d8e03a', '#c2d42e', '#e0d838'];

/** Armed local per biome: valley farmer (cap), mesa rancher (stetson), snow
 *  trapper (fur hood), moor gamekeeper (flat cap), town warden (hi-vis). */
export function buildNormie(variant: number, biome: BiomeId = 'valley'): NormieRig {
  const group = new THREE.Group();
  const skin = new THREE.MeshLambertMaterial({
    color: biome === 'mesa' ? '#c98e66' : biome === 'snow' ? '#e8c4a8' : '#d9a184',
    flatShading: true,
  });
  const offWorld = biome === 'mars' || biome === 'moon';
  const shirts = biome === 'mesa' ? MESA_SHIRTS
    : biome === 'snow' ? SNOW_PARKAS
      : biome === 'moor' ? MOOR_TWEEDS
        : biome === 'town' ? TOWN_HIVIS
          : offWorld ? SUIT_WHITES : NORMIE_SHIRTS;
  const torsoMat = new THREE.MeshLambertMaterial({ color: shirts[variant % shirts.length], flatShading: true });
  const denim = new THREE.MeshLambertMaterial({
    color: biome === 'snow' ? '#3a3f4a' : biome === 'town' ? '#232830' : biome === 'moor' ? '#4a4230' : '#2c3a54',
    flatShading: true,
  });

  const legGeo = new THREE.CylinderGeometry(0.09, 0.11, 0.62, 5);
  for (const dx of [-0.13, 0.13]) {
    const leg = new THREE.Mesh(legGeo, denim);
    leg.position.set(dx, 0.31, 0);
    group.add(leg);
  }
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.62, 0.3), torsoMat);
  torso.position.y = 0.93;
  group.add(torso);
  const armGeo = new THREE.CylinderGeometry(0.06, 0.07, 0.5, 5);
  for (const dx of [-0.3, 0.3]) {
    const arm = new THREE.Mesh(armGeo, torsoMat);
    arm.position.set(dx, 0.95, 0.08);
    arm.rotation.x = -0.7;
    group.add(arm);
  }
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.3, 0.28), skin);
  head.position.y = 1.42;
  group.add(head);
  if (offWorld) {
    // Helmet: a gold-visored fishbowl, a life-support pack, and a chest light.
    // He is not out here to shoot at anybody, but he brought the gun anyway.
    const helmet = new THREE.Mesh(
      new THREE.SphereGeometry(0.26, 10, 8),
      new THREE.MeshLambertMaterial({ color: '#cfe6ff', transparent: true, opacity: 0.42, depthWrite: false }),
    );
    helmet.position.y = 1.44;
    const visor = new THREE.Mesh(
      new THREE.SphereGeometry(0.24, 10, 8, Math.PI * 0.25, Math.PI * 0.5, Math.PI * 0.28, Math.PI * 0.44),
      new THREE.MeshLambertMaterial({ color: '#f7c04a', emissive: '#7a5a10', flatShading: true }),
    );
    visor.position.y = 1.44;
    visor.rotation.y = Math.PI * 0.75;
    const pack = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.44, 0.2),
      new THREE.MeshLambertMaterial({ color: '#9aa0aa', flatShading: true }),
    );
    pack.position.set(0, 0.98, -0.22);
    const lamp = makeGlowSprite(1.1);
    lamp.material.color.set('#eaf4ff');
    lamp.material.opacity = 0.55;
    lamp.position.set(0, 1.06, 0.2);
    group.add(helmet, visor, pack, lamp);
  } else if (biome === 'mesa') {
    // Stetson: wide brim, tall crown.
    const hatMat = new THREE.MeshLambertMaterial({ color: '#6b4a2c', flatShading: true });
    const hatBrim = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.04, 10), hatMat);
    hatBrim.position.y = 1.58;
    const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.16, 0.2, 8), hatMat);
    crown.position.y = 1.7;
    group.add(hatBrim, crown);
  } else if (biome === 'moor') {
    // Flat cap and a tweed shoulder cape: the keeper, and it is his hill.
    const capMat = new THREE.MeshLambertMaterial({ color: '#4a4634', flatShading: true });
    const cap = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.1, 0.3), capMat);
    cap.position.y = 1.59;
    cap.rotation.z = 0.06;
    const peak = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.04, 0.16), capMat);
    peak.position.set(0, 1.55, 0.22);
    const cape = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.2, 0.4), torsoMat);
    cape.position.y = 1.16;
    group.add(cap, peak, cape);
  } else if (biome === 'town') {
    // Peaked cap, clipboard, and a vest that catches the moonlight.
    const capMat = new THREE.MeshLambertMaterial({ color: '#181c24', flatShading: true });
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.18, 0.14, 9), capMat);
    cap.position.y = 1.62;
    const peak = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.035, 0.16), capMat);
    peak.position.set(0, 1.56, 0.2);
    const band = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.05, 0.34), new THREE.MeshLambertMaterial({ color: '#d8e03a', flatShading: true }));
    band.position.y = 1.55;
    // Retro-reflective bands across the vest.
    const stripeMat = new THREE.MeshBasicMaterial({ color: '#f4f8dc' });
    for (const sy of [0.82, 1.02]) {
      const stripe = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.07), stripeMat);
      stripe.position.set(0, sy, 0.16);
      group.add(stripe);
    }
    const board = new THREE.Mesh(
      new THREE.BoxGeometry(0.26, 0.34, 0.03),
      new THREE.MeshLambertMaterial({ color: '#c9bc94', flatShading: true }),
    );
    board.position.set(-0.34, 0.9, 0.24);
    board.rotation.x = -0.5;
    group.add(cap, peak, band, board);
  } else if (biome === 'snow') {
    // Fur-trimmed hood pulled up against the cold.
    const fur = new THREE.Mesh(
      new THREE.TorusGeometry(0.19, 0.07, 6, 14),
      new THREE.MeshLambertMaterial({ color: '#e8e4da', flatShading: true }),
    );
    fur.position.set(0, 1.44, 0.02);
    fur.rotation.x = Math.PI / 2 - 0.35;
    const hood = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.55), torsoMat);
    hood.position.y = 1.46;
    group.add(fur, hood);
  } else {
    const cap = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.12, 0.32), new THREE.MeshLambertMaterial({ color: '#b03030', flatShading: true }));
    cap.position.y = 1.6;
    const brim = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.04, 0.18), cap.material);
    brim.position.set(0, 1.56, 0.24);
    group.add(cap, brim);
  }

  // Shotgun held across, tilts up when aiming.
  const gun = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.045, 0.85, 5), new THREE.MeshLambertMaterial({ color: '#3a2e20', flatShading: true }));
  gun.position.set(0.05, 1.05, 0.3);
  gun.rotation.x = -1.15;
  const warning = makeThreatWarning();
  warning.position.y = 3;
  group.add(gun, warning);

  // Legs, clothes and hat never articulate independently. Bake them into one
  // mesh per material while keeping the gun and warning separate for aiming.
  // This preserves the exact silhouette but removes several draw calls from
  // every late-wave shooter.
  group.remove(gun, warning);
  const baked = bakeProto(group);
  group.clear();
  group.add(...baked.children, gun, warning);

  return { group, gun, torsoMat, warning };
}

/* ---------------- abductable animals ---------------- */

const animalProtos = new Map<AnimalSpecies, THREE.Group>();

/**
 * Collapse a built animal into one mesh per material.
 *
 * A herd is sixteen head, and a beast assembled from a dozen little boxes was
 * a dozen draw calls every time one of them was on screen — livestock alone
 * came to most of the frame's budget. Baking each proto down once at build
 * time makes a cow four draw calls however many parts went into it, which is
 * what pays for it having eyes, hooves and a tail at all.
 *
 * Materials survive the bake, so buildRedHeifer can still recolour her by
 * matching the coat, patch and horn colours.
 */
export function bakeProto(group: THREE.Group): THREE.Group {
  group.updateMatrixWorld(true);
  const buckets = new Map<THREE.Material, THREE.BufferGeometry[]>();
  group.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const geo = o.geometry.clone().applyMatrix4(o.matrixWorld);
    const material = o.material as THREE.Material;
    const bucket = buckets.get(material);
    if (bucket) bucket.push(geo);
    else buckets.set(material, [geo]);
  });
  const baked = new THREE.Group();
  for (const [material, geos] of buckets) baked.add(new THREE.Mesh(mergeStaticGeometries(geos), material));
  return baked;
}

/**
 * Merge the ordinary attributes used by static low-poly rigs. Polyhedron
 * geometries (including the drone's octahedral hull) are deliberately
 * non-indexed, while boxes/cylinders are indexed; a biome kit can put both in
 * one material bucket. Treat an absent index as one sequential triangle stream
 * instead of assuming every primitive owns `geometry.index`.
 */
export function mergeStaticGeometries(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  if (geos.length === 1) return geos[0];
  const position: number[] = [];
  const normal: number[] = [];
  const uv: number[] = [];
  const index: number[] = [];
  for (const geo of geos) {
    const base = position.length / 3;
    const p = geo.getAttribute('position');
    if (!p) throw new Error('Static rig geometry is missing positions');
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    const n = geo.getAttribute('normal');
    if (!n) throw new Error('Static rig geometry is missing normals');
    const t = geo.getAttribute('uv');
    for (let i = 0; i < p.count; i++) position.push(p.getX(i), p.getY(i), p.getZ(i));
    for (let i = 0; i < n.count; i++) normal.push(n.getX(i), n.getY(i), n.getZ(i));
    if (t) {
      for (let i = 0; i < t.count; i++) uv.push(t.getX(i), t.getY(i));
    } else {
      for (let i = 0; i < p.count; i++) uv.push(0, 0);
    }
    const idx = geo.getIndex();
    if (idx) {
      for (let i = 0; i < idx.count; i++) index.push(base + idx.getX(i));
    } else {
      for (let i = 0; i < p.count; i++) index.push(base + i);
    }
    geo.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(new Float32Array(position), 3));
  out.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normal), 3));
  out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  out.setIndex(index);
  return out;
}

/**
 * The Friesian. She is the original abductable and the game's mascot, and for
 * a long time she was the plainest thing in the herd: a box with two side
 * patches, no neck, no eyes, no tail. Everything the later beasts learnt is
 * here now — a rounded rump, a neck that carries the head forward, hooves, a
 * switch tail — and the patches have come up onto her back, because the camera
 * is above her and a marking on the flank is a marking nobody ever sees.
 *
 * Colours are load-bearing: buildRedHeifer recolours by matching the horn bone
 * and the patch black, so the extremities that should stay dark on her share
 * the patch material rather than owning one of their own.
 */
function buildCowProto(): THREE.Group {
  const g = new THREE.Group();
  const coat = new THREE.MeshLambertMaterial({ color: '#e8e4da', flatShading: true });
  const patch = new THREE.MeshLambertMaterial({ color: '#26232a', flatShading: true });
  const pink = new THREE.MeshLambertMaterial({ color: '#d99a94', flatShading: true });
  const bone = new THREE.MeshLambertMaterial({ color: '#cbc2ae', flatShading: true });

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.46, 0.9, 0.82), coat);
  body.position.y = 0.88;
  const rump = new THREE.Mesh(new THREE.SphereGeometry(0.45, 9, 7), coat);
  rump.scale.set(0.85, 1, 0.95);
  rump.position.set(-0.7, 0.9, 0);
  // The withers: the shoulder ridge that stops her reading as a loaf.
  const withers = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.24, 0.7), coat);
  withers.position.set(0.44, 1.34, 0);
  g.add(body, rump, withers);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.31, 0.52, 7), coat);
  neck.position.set(0.84, 1.24, 0);
  neck.rotation.z = -0.62;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.42, 0.44), coat);
  head.position.set(1.14, 1.36, 0);
  head.rotation.z = -0.16;
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.26, 0.36), pink);
  muzzle.position.set(1.4, 1.22, 0);
  muzzle.rotation.z = -0.16;
  g.add(neck, head, muzzle);

  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.058, 5, 4), patch);
    eye.position.set(1.26, 1.46, side * 0.19);
    const ear = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.13, 0.26), coat);
    ear.position.set(1.0, 1.45, side * 0.28);
    ear.rotation.x = side * 0.42;
    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.055, 0.21, 4), bone);
    horn.position.set(1.07, 1.62, side * 0.15);
    horn.rotation.z = -0.12;
    horn.rotation.x = side * 0.5;
    g.add(eye, ear, horn);
  }

  // Markings: two on the flanks, two along the spine where they can be seen.
  for (const [px, py, pz, pw, ph, pd] of [
    [-0.3, 0.98, 0, 0.55, 0.5, 0.86],
    [0.42, 0.86, -0.2, 0.4, 0.88, 0.52],
    [0.02, 1.32, 0.05, 0.66, 0.14, 0.52],
    [-0.6, 1.28, -0.14, 0.36, 0.14, 0.38],
  ] as const) {
    const mark = new THREE.Mesh(new THREE.BoxGeometry(pw, ph, pd), patch);
    mark.position.set(px, py, pz);
    g.add(mark);
  }

  const legGeo = new THREE.CylinderGeometry(0.09, 0.082, 0.46, 5);
  const hoofGeo = new THREE.CylinderGeometry(0.095, 0.1, 0.15, 5);
  for (const [lx, lz] of [[-0.55, -0.28], [-0.55, 0.28], [0.55, -0.28], [0.55, 0.28]]) {
    const leg = new THREE.Mesh(legGeo, coat);
    leg.position.set(lx, 0.33, lz);
    const hoof = new THREE.Mesh(hoofGeo, patch);
    hoof.position.set(lx, 0.075, lz);
    g.add(leg, hoof);
  }

  const udder = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), pink);
  udder.position.set(-0.28, 0.5, 0);
  udder.scale.y = 0.7;
  const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.03, 0.66, 5), coat);
  tail.position.set(-0.86, 0.74, 0);
  tail.rotation.z = 0.3;
  const switchEnd = new THREE.Mesh(new THREE.SphereGeometry(0.095, 6, 5), patch);
  switchEnd.position.set(-0.97, 0.44, 0);
  g.add(udder, tail, switchEnd);
  return g;
}

function buildGoatProto(): THREE.Group {
  const g = new THREE.Group();
  const coat = new THREE.MeshLambertMaterial({ color: '#c79b68', flatShading: true });
  const cream = new THREE.MeshLambertMaterial({ color: '#e8dcc5', flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: '#4b3528', flatShading: true });
  const hornMat = new THREE.MeshLambertMaterial({ color: '#a99b83', flatShading: true });

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.25, 0.62, 0.58), coat);
  body.position.y = 0.88;
  const chest = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.68, 0.52), cream);
  chest.position.set(0.42, 0.96, 0);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.38, 0.38), coat);
  head.position.set(0.86, 1.36, 0);
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.2, 0.3), dark);
  muzzle.position.set(1.16, 1.28, 0);
  const beard = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.42, 6), dark);
  beard.position.set(0.98, 1.02, 0);
  g.add(body, chest, head, muzzle, beard);

  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.07, 0.3), coat);
    ear.position.set(0.76, 1.52, side * 0.28);
    ear.rotation.x = side * 0.25;
    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.48, 6), hornMat);
    horn.position.set(0.69, 1.75, side * 0.15);
    horn.rotation.z = -0.35;
    horn.rotation.x = side * 0.18;
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 5, 4), dark);
    eye.position.set(1.0, 1.44, side * 0.16);
    g.add(ear, horn, eye);
  }

  const legGeo = new THREE.CylinderGeometry(0.07, 0.08, 0.62, 5);
  for (const [lx, lz] of [[-0.45, -0.21], [-0.45, 0.21], [0.43, -0.21], [0.43, 0.21]]) {
    const leg = new THREE.Mesh(legGeo, dark);
    leg.position.set(lx, 0.33, lz);
    g.add(leg);
  }
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.42, 5), cream);
  tail.position.set(-0.78, 1.15, 0);
  tail.rotation.z = -0.9;
  g.add(tail);
  return g;
}

function addAntlerRod(
  group: THREE.Group,
  from: THREE.Vector3,
  to: THREE.Vector3,
  material: THREE.Material,
): void {
  const direction = to.clone().sub(from);
  const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, direction.length(), 5), material);
  rod.position.copy(from).add(to).multiplyScalar(0.5);
  rod.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  group.add(rod);
}

/**
 * The Donkey Farm's resident. Read against the goat beside it, a donkey is all
 * ears and muzzle: the ears are deliberately oversized and the legs stubby,
 * because at beam range the silhouette is the only thing that identifies a
 * species, and a correctly-proportioned donkey just reads as a small horse.
 */
function buildDonkeyProto(): THREE.Group {
  const g = new THREE.Group();
  const coat = new THREE.MeshLambertMaterial({ color: '#9a9187', flatShading: true });
  const pale = new THREE.MeshLambertMaterial({ color: '#ded5c6', flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: '#4a423b', flatShading: true });

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.32, 0.72, 0.62), coat);
  body.position.y = 0.92;
  const belly = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.3, 0.56), pale);
  belly.position.y = 0.66;
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.28, 0.6, 7), coat);
  neck.position.set(0.56, 1.3, 0);
  neck.rotation.z = -0.36;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.42, 0.36), coat);
  head.position.set(0.84, 1.58, 0);
  // The muzzle is the giveaway: pale, blunt and far too big for the head.
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.3, 0.32), pale);
  muzzle.position.set(1.12, 1.46, 0);
  const nose = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.14, 0.22), dark);
  nose.position.set(1.31, 1.44, 0);
  // Cross of the donkey: the dark stripe down the spine, and the bristle mane.
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.08, 0.12), dark);
  stripe.position.y = 1.28;
  const mane = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.2, 0.14), dark);
  mane.position.set(0.6, 1.56, 0);
  mane.rotation.z = -0.36;
  g.add(body, belly, neck, head, muzzle, nose, stripe, mane);

  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.62, 0.1), coat);
    ear.position.set(0.78, 2.0, side * 0.16);
    ear.rotation.z = side * 0.06;
    ear.rotation.x = side * 0.22;
    const earInner = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.42, 0.06), dark);
    earInner.position.set(0.8, 2.0, side * 0.16);
    earInner.rotation.x = side * 0.22;
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.06, 5, 4), dark);
    eye.position.set(1.0, 1.66, side * 0.16);
    g.add(ear, earInner, eye);
  }

  const legGeo = new THREE.CylinderGeometry(0.08, 0.09, 0.56, 5);
  for (const [lx, lz] of [[-0.46, -0.22], [-0.46, 0.22], [0.46, -0.22], [0.46, 0.22]]) {
    const leg = new THREE.Mesh(legGeo, coat);
    leg.position.set(lx, 0.3, lz);
    const hoof = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.1, 0.12, 5), dark);
    hoof.position.set(lx, 0.06, lz);
    g.add(leg, hoof);
  }
  // A rope of a tail with a tuft on the end, not a goat's cone.
  const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.5, 5), coat);
  tail.position.set(-0.7, 1.02, 0);
  tail.rotation.z = 0.45;
  const tuft = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.22, 0.14), dark);
  tuft.position.set(-0.83, 0.78, 0);
  g.add(tail, tuft);
  return g;
}

function buildReindeerProto(): THREE.Group {
  const g = new THREE.Group();
  const coat = new THREE.MeshLambertMaterial({ color: '#765039', flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: '#3b2b25', flatShading: true });
  const cream = new THREE.MeshLambertMaterial({ color: '#c8b79d', flatShading: true });
  const antler = new THREE.MeshLambertMaterial({ color: '#b8aa8d', flatShading: true });

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.38, 0.68, 0.62), coat);
  body.position.y = 1.02;
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.3, 0.86, 7), coat);
  neck.position.set(0.52, 1.42, 0);
  neck.rotation.z = -0.22;
  const chest = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.64, 0.56), cream);
  chest.position.set(0.48, 1.12, 0);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.38, 0.38), coat);
  head.position.set(0.78, 1.82, 0);
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.22, 0.3), cream);
  muzzle.position.set(1.1, 1.74, 0);
  const nose = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.16, 0.25), dark);
  nose.position.set(1.31, 1.73, 0);
  g.add(body, neck, chest, head, muzzle, nose);

  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.38, 5), coat);
    ear.position.set(0.61, 2.05, side * 0.25);
    ear.rotation.x = side * 0.55;
    ear.rotation.z = 0.55;
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 5, 4), dark);
    eye.position.set(0.92, 1.9, side * 0.16);
    g.add(ear, eye);
    const z = side * 0.16;
    addAntlerRod(g, new THREE.Vector3(0.67, 2.02, z), new THREE.Vector3(0.43, 2.5, z), antler);
    addAntlerRod(g, new THREE.Vector3(0.54, 2.28, z), new THREE.Vector3(0.82, 2.55, z), antler);
    addAntlerRod(g, new THREE.Vector3(0.45, 2.46, z), new THREE.Vector3(0.2, 2.7, z), antler);
  }

  const legGeo = new THREE.CylinderGeometry(0.065, 0.08, 0.82, 5);
  for (const [lx, lz] of [[-0.5, -0.22], [-0.5, 0.22], [0.48, -0.22], [0.48, 0.22]]) {
    const leg = new THREE.Mesh(legGeo, dark);
    leg.position.set(lx, 0.43, lz);
    g.add(leg);
  }
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.35, 5), cream);
  tail.position.set(-0.84, 1.24, 0);
  tail.rotation.z = -1.05;
  g.add(tail);
  return g;
}

function buildSheepProto(): THREE.Group {
  const g = new THREE.Group();
  const fleece = new THREE.MeshLambertMaterial({ color: '#e4e0d4', flatShading: true });
  const face = new THREE.MeshLambertMaterial({ color: '#3a3630', flatShading: true });
  const hoof = new THREE.MeshLambertMaterial({ color: '#2c2924', flatShading: true });

  // The fleece is three overlapping lumps — a sheep is a cloud on legs.
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.52, 9, 7), fleece);
  body.scale.set(1.45, 1.05, 1.02);
  body.position.set(-0.05, 0.86, 0);
  const rump = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 6), fleece);
  rump.position.set(-0.55, 0.92, 0);
  const shoulder = new THREE.Mesh(new THREE.SphereGeometry(0.36, 8, 6), fleece);
  shoulder.position.set(0.42, 0.95, 0);
  g.add(body, rump, shoulder);

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.32, 0.3), face);
  head.position.set(0.82, 1.06, 0);
  head.rotation.z = -0.12;
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.18, 0.22), face);
  muzzle.position.set(1.05, 0.97, 0);
  // A woolly cap over the brow — the bit that makes it read as sheep, not goat.
  const cap = new THREE.Mesh(new THREE.SphereGeometry(0.2, 7, 5), fleece);
  cap.position.set(0.72, 1.22, 0);
  cap.scale.set(1, 0.8, 1.05);
  g.add(head, muzzle, cap);

  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.07, 0.22), face);
    ear.position.set(0.74, 1.13, side * 0.22);
    ear.rotation.x = side * 0.5;
    // Pale eyes: the only thing that shows on a dark face at this size.
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 5, 4), fleece);
    eye.position.set(0.96, 1.12, side * 0.13);
    g.add(ear, eye);
  }

  const legGeo = new THREE.CylinderGeometry(0.055, 0.065, 0.56, 5);
  for (const [lx, lz] of [[-0.44, -0.2], [-0.44, 0.2], [0.4, -0.2], [0.4, 0.2]]) {
    const leg = new THREE.Mesh(legGeo, hoof);
    leg.position.set(lx, 0.3, lz);
    g.add(leg);
  }
  const tail = new THREE.Mesh(new THREE.SphereGeometry(0.13, 6, 5), fleece);
  tail.position.set(-0.95, 0.95, 0);
  g.add(tail);
  return g;
}

function buildPigProto(): THREE.Group {
  const g = new THREE.Group();
  const skin = new THREE.MeshLambertMaterial({ color: '#dd9a95', flatShading: true });
  const snoutMat = new THREE.MeshLambertMaterial({ color: '#c47d7d', flatShading: true });
  const trotter = new THREE.MeshLambertMaterial({ color: '#4a3230', flatShading: true });

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.44, 1.25, 10), skin);
  body.rotation.z = Math.PI / 2;
  body.position.y = 0.62;
  const rear = new THREE.Mesh(new THREE.SphereGeometry(0.44, 9, 7), skin);
  rear.position.set(-0.6, 0.62, 0);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.4, 0.42), skin);
  head.position.set(0.78, 0.66, 0);
  const snout = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.19, 0.18, 9), snoutMat);
  snout.rotation.z = Math.PI / 2;
  snout.position.set(1.05, 0.6, 0);
  g.add(body, rear, head, snout);

  // Ears splayed rather than upright: from the game's overhead camera a pig is
  // a pink barrel, and these are what give it a front end.
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.17, 0.34, 4), snoutMat);
    ear.position.set(0.66, 0.86, side * 0.22);
    ear.rotation.x = side * 0.95;
    ear.rotation.z = -0.5;
    g.add(ear);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 5, 4), trotter);
    eye.position.set(0.93, 0.78, side * 0.16);
    g.add(eye);
  }
  // A darker saddle down the spine, so the beast has a direction from above.
  const saddle = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.1, 0.44), snoutMat);
  saddle.position.set(-0.15, 1.0, 0);
  g.add(saddle);

  const legGeo = new THREE.CylinderGeometry(0.09, 0.1, 0.4, 5);
  for (const [lx, lz] of [[-0.42, -0.24], [-0.42, 0.24], [0.42, -0.24], [0.42, 0.24]]) {
    const leg = new THREE.Mesh(legGeo, trotter);
    leg.position.set(lx, 0.2, lz);
    g.add(leg);
  }
  // Curly tail: half a torus stood on end.
  const tail = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.035, 5, 10, Math.PI * 1.5), skin);
  tail.position.set(-1.0, 0.78, 0);
  tail.rotation.y = Math.PI / 2;
  g.add(tail);
  return g;
}

/**
 * The grey. Not livestock by any reasonable definition, but the mothership
 * pays by the head and does not ask questions, and Cydonia is full of them.
 */
function buildMartianProto(): THREE.Group {
  const g = new THREE.Group();
  const skin = new THREE.MeshLambertMaterial({ color: '#9fc7a8', flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: '#141a18', flatShading: true });

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.46, 10, 8), skin);
  head.scale.set(0.92, 1.05, 0.86);
  head.position.set(0.06, 1.34, 0);
  // The jaw tapers to a point: the top half of the silhouette is all cranium.
  const jaw = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.5, 8), skin);
  jaw.rotation.x = Math.PI;
  jaw.position.set(0.1, 1.02, 0);
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 0.24, 6), skin);
  neck.position.set(0.05, 0.86, 0);
  const torso = new THREE.Mesh(new THREE.SphereGeometry(0.28, 9, 7), skin);
  torso.scale.set(0.8, 1.5, 0.62);
  torso.position.set(0, 0.6, 0);
  g.add(head, jaw, neck, torso);

  for (const side of [-1, 1]) {
    // Almond eyes, wrapped round the head and tilted.
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.17, 8, 6), dark);
    eye.scale.set(0.55, 0.95, 0.42);
    eye.position.set(0.36, 1.34, side * 0.2);
    eye.rotation.z = -0.5;
    eye.rotation.y = side * -0.5;
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.04, 0.72, 5), skin);
    arm.position.set(-0.02, 0.58, side * 0.24);
    arm.rotation.x = side * -0.22;
    arm.rotation.z = 0.2;
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.05, 0.5, 5), skin);
    leg.position.set(0, 0.25, side * 0.11);
    g.add(eye, arm, leg);
  }
  return g;
}

/** The cow that jumped over the moon, still up here, in a fishbowl. */
function buildMoonCowProto(): THREE.Group {
  const g = buildCowProto();
  const helmet = new THREE.Mesh(
    new THREE.SphereGeometry(0.52, 12, 9),
    new THREE.MeshLambertMaterial({
      color: '#bfe4ff', transparent: true, opacity: 0.34, depthWrite: false, side: THREE.DoubleSide,
    }),
  );
  helmet.position.set(1.16, 1.36, 0);
  const collar = new THREE.Mesh(
    new THREE.CylinderGeometry(0.3, 0.3, 0.12, 10),
    new THREE.MeshLambertMaterial({ color: '#c8ccd4', flatShading: true }),
  );
  collar.position.set(0.94, 1.16, 0);
  collar.rotation.z = 0.5;
  const tank = new THREE.Mesh(
    new THREE.CylinderGeometry(0.19, 0.19, 0.72, 8),
    new THREE.MeshLambertMaterial({ color: '#d8dce4', flatShading: true }),
  );
  tank.rotation.z = Math.PI / 2;
  tank.position.set(-0.1, 1.46, 0);
  g.add(helmet, collar, tank);
  return g;
}

export function buildAnimal(species: AnimalSpecies): THREE.Group {
  let proto = animalProtos.get(species);
  if (!proto) {
    proto = bakeProto(species === 'goat' ? buildGoatProto()
      : species === 'reindeer' ? buildReindeerProto()
        : species === 'sheep' ? buildSheepProto()
          : species === 'pig' ? buildPigProto()
            : species === 'donkey' ? buildDonkeyProto()
              : species === 'martian' ? buildMartianProto()
                : species === 'mooncow' ? buildMoonCowProto() : buildCowProto());
    animalProtos.set(species, proto);
  }
  const animal = proto.clone();
  animal.userData.species = species;
  return animal;
}

/** The Red Heifer: entirely red, without blemish, never yoked. Billed in-game as the Golden Calf. 1000 points.
 *  She is the same calf in every land — always the valley silhouette. */
export function buildRedHeifer(): THREE.Group {
  const cow = buildAnimal('cow');
  const coat = new THREE.MeshPhongMaterial({
    color: '#9e3320',
    emissive: '#3a0e08',
    specular: '#ffb08a',
    shininess: 60,
  });
  const darkCoat = new THREE.MeshPhongMaterial({
    color: '#7c2718',
    emissive: '#2a0a06',
    specular: '#e89a70',
    shininess: 50,
  });
  const bone = new THREE.MeshPhongMaterial({ color: '#d8cfb8', specular: '#fff6e0', shininess: 40 });
  cow.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      const c = (o.material as THREE.MeshLambertMaterial).color.getHexString();
      // Even two hairs of another colour would disqualify her — horns excepted.
      o.material = c === 'cbc2ae' ? bone : c === '26232a' ? darkCoat : coat;
    }
  });
  const halo = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexRed(),
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  halo.scale.set(3.4, 3.4, 1);
  halo.position.y = 1;
  cow.add(halo);
  return cow;
}

/**
 * The tell on a loose donkey: a brand-orange aura marking it as stock that
 * belongs behind the rails rather than in the hold. Tighter than a cult
 * leader's, and dimmed once the animal is back in the pen.
 *
 * Tinted white glow rather than the shared orange texture on purpose. That
 * texture's core is a pale warm yellow that reads as a wash of light over a
 * grey coat; multiplying a neutral falloff by #f7931a keeps the orange all the
 * way into the centre, so the animal itself looks lit rather than hazy.
 */
export function makeDonkeyAura(): THREE.Sprite {
  const aura = makeGlowSprite(2.9);
  aura.material.color.set('#f7931a');
  aura.material.blending = THREE.AdditiveBlending;
  aura.material.opacity = 0.28;
  aura.position.y = 1;
  return aura;
}

/* ---------------- cultists ---------------- */

export interface CultistRig {
  group: THREE.Group;
  /** Immediate low-poly 3D stand-in; detached once the detailed GLB arrives. */
  plane: THREE.Group;
  member: CultMember;
  is3d: boolean;
  isDetailed: boolean;
}

let cult3dModule: Promise<typeof import('./cult3d')> | null = null;

function loadCult3dRuntime(): Promise<typeof import('./cult3d')> {
  if (!cult3dModule) {
    const pending = import('./cult3d');
    cult3dModule = pending;
    // A transient chunk failure must not condemn every later cultist to its
    // low-poly proxy for the rest of the session. The next request retries.
    void pending.catch(() => {
      if (cult3dModule === pending) cult3dModule = null;
    });
  }
  return cult3dModule;
}

/** Warm the GLTF + meshopt runtime behind the launch loader. The title bundle
 * stays lean, but the first rendered cultists no longer wait for code before
 * their model requests can even begin. */
export function preloadCult3dRuntime(): Promise<void> {
  return loadCult3dRuntime().then(() => undefined);
}

/** Gate the opening presentation on one real character, not just its decoder. */
export function preloadCult3dModel(id: string): Promise<void> {
  return loadCult3dRuntime()
    .then((module) => module.loadCultModel(id))
    .then(() => undefined);
}

function instantiateCultModelLazy(id: string): Promise<THREE.Group | null> {
  return loadCult3dRuntime()
    .then((module) => module.instantiateCultModel(id))
    .catch(() => null);
}

/** A real lit silhouette for slow/offline connections. Never flash a portrait
 * plane into the world while the detailed character downloads. */
function buildCultProxy(): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.ConeGeometry(.48, 1.65, 7),
    new THREE.MeshLambertMaterial({ color: '#dc6d19', flatShading: true }));
  body.position.y = .83;
  group.add(body);
  return group;
}

export function buildCultist(member: CultMember): CultistRig {
  const plane = buildCultProxy();
  // Soft orange aura so cultists read at distance.
  const aura = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexOrange(),
    transparent: true,
    opacity: 0.3,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  aura.scale.set(2.4, 2.4, 1);
  aura.position.y = 1;
  const group = new THREE.Group();
  group.add(plane, aura);
  const rig: CultistRig = { group, plane, member, is3d: true, isDetailed: false };
  // Upgrade to the official 3D model as soon as it arrives; the proxy and aura
  // keep the character genuinely three-dimensional throughout the handoff.
  void instantiateCultModelLazy(member.id).then((model) => {
    if (!model) return;
    group.remove(plane);
    group.add(model);
    rig.isDetailed = true;
  });
  return rig;
}

/** Pick a member not currently on the field. */
export function pickMember(exclude: Set<string>): CultMember {
  const available = ROSTER.filter((m) => !exclude.has(m.id));
  const pool = available.length > 0 ? available : ROSTER;
  return pool[Math.floor(Math.random() * pool.length)];
}

/* ---------------- drones ---------------- */

export interface DroneRig {
  group: THREE.Group;
  rotor: THREE.Mesh;
  eyeMat: THREE.MeshBasicMaterial;
  bodyMat: THREE.MeshLambertMaterial;
  warning: THREE.Sprite;
}

/** Fiat patrol per biome: valley quad, mesa buzzard wing, snow sentry on skids,
 *  moor lantern drone, town CCTV unit. */
export function buildDrone(biome: BiomeId = 'valley'): DroneRig {
  const group = new THREE.Group();
  const bodyMat = new THREE.MeshLambertMaterial({
    color: biome === 'mesa' ? '#4a3424'
      : biome === 'snow' ? '#4a5a74'
        : biome === 'moor' ? '#3a4230'
          : biome === 'town' ? '#2e333c' : '#39304a',
    flatShading: true,
  });
  const body = new THREE.Mesh(
    biome === 'town' ? new THREE.BoxGeometry(1.1, 0.62, 0.86) : new THREE.OctahedronGeometry(0.7, 0),
    bodyMat,
  );
  if (biome !== 'town') body.scale.y = 0.72;
  group.add(body);
  const rotor = new THREE.Mesh(
    // Twelve circumference segments are ample at this on-screen size. The old
    // eighteen spent 288 extra triangles across the four late-wave drones.
    new THREE.TorusGeometry(0.95, 0.07, 6, 12),
    new THREE.MeshLambertMaterial({
      color: biome === 'mesa' ? '#2c1d12'
        : biome === 'snow' ? '#2a3446'
          : biome === 'moor' ? '#22281c'
            : biome === 'town' ? '#171a20' : '#191d2c',
      flatShading: true,
    }),
  );
  rotor.rotation.x = Math.PI / 2;
  group.add(rotor);
  if (biome === 'moor') {
    // Coaxial second disc and a swinging hurricane lamp — it is out beating
    // the heather for whatever the keeper has not found yet.
    const upper = new THREE.Mesh(
      new THREE.TorusGeometry(0.66, 0.05, 6, 14),
      new THREE.MeshLambertMaterial({ color: '#22281c', flatShading: true }),
    );
    upper.rotation.x = Math.PI / 2;
    upper.position.y = 0.34;
    const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.42, 5), bodyMat);
    stalk.position.set(0, -0.5, 0.22);
    const lampGlass = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.14, 0.24, 8),
      new THREE.MeshBasicMaterial({ color: '#ffd98a' }),
    );
    lampGlass.position.set(0, -0.74, 0.22);
    const lampGlow = makeGlowSprite(1.6);
    lampGlow.material.color.set('#ffc46a');
    lampGlow.material.opacity = 0.42;
    lampGlow.position.set(0, -0.74, 0.22);
    group.add(upper, stalk, lampGlass, lampGlow);
  } else if (biome === 'town') {
    // Municipal CCTV: housing, lens barrel, and a blue strobe on the mast.
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.19, 0.22, 0.5, 10),
      new THREE.MeshLambertMaterial({ color: '#1d222a', flatShading: true }),
    );
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, -0.08, 0.62);
    const hood = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.1, 0.44), bodyMat);
    hood.position.set(0, 0.24, 0.4);
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.04, 0.34, 5), bodyMat);
    mast.position.y = 0.5;
    const strobe = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 0.1, 0.14),
      new THREE.MeshBasicMaterial({ color: '#5aa8ff' }),
    );
    strobe.position.y = 0.7;
    const strobeGlow = makeGlowSprite(1.5);
    strobeGlow.material.color.set('#5aa8ff');
    strobeGlow.material.opacity = 0.35;
    strobeGlow.position.y = 0.7;
    group.add(barrel, hood, mast, strobe, strobeGlow);
  }
  if (biome === 'mesa') {
    // Buzzard plates: fixed wings raked back like a circling vulture.
    const wingMat = new THREE.MeshLambertMaterial({ color: '#3a2a1a', flatShading: true });
    for (const side of [-1, 1]) {
      const wing = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.05, 0.42), wingMat);
      wing.position.set(side * 0.85, 0.08, -0.1);
      wing.rotation.z = side * 0.22;
      wing.rotation.y = side * -0.25;
      group.add(wing);
    }
  } else if (biome === 'snow') {
    // Landing skids for the drifts.
    const skidMat = new THREE.MeshLambertMaterial({ color: '#1d2534', flatShading: true });
    for (const side of [-1, 1]) {
      const skid = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.06, 1.05), skidMat);
      skid.position.set(side * 0.42, -0.58, 0);
      group.add(skid);
      const strut = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.3, 0.06), skidMat);
      strut.position.set(side * 0.42, -0.4, 0);
      group.add(strut);
    }
  }
  const eyeMat = new THREE.MeshBasicMaterial({ color: '#ff4040' });
  const eye = new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 6), eyeMat);
  eye.position.set(0, -0.12, 0.62);
  group.add(eye);
  // Dollar plate: fiat drone branding.
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.5),
    new THREE.MeshBasicMaterial({ map: makeDollarTexture(), transparent: true }),
  );
  plate.position.set(0, 0.45, 0);
  plate.rotation.x = -Math.PI / 2;
  const warning = makeThreatWarning();
  warning.position.y = 2.25;
  group.add(plate, warning);

  // The hull kit never deforms. Keep only the animated rotor, warning, eye and
  // additive glows as separate objects; bake the rest by material exactly as
  // the herd rigs are baked below.
  const glows = group.children.filter((node) => node instanceof THREE.Sprite && node !== warning);
  group.remove(rotor, eye, warning, ...glows);
  const baked = bakeProto(group);
  group.clear();
  group.add(...baked.children, rotor, eye, ...glows, warning);
  return { group, rotor, eyeMat, bodyMat, warning };
}

export function makeDollarTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d')!;
  g.fillStyle = '#4c9950';
  g.beginPath();
  g.arc(32, 32, 30, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#dff5da';
  g.font = 'bold 40px sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText('$', 32, 34);
  return new THREE.CanvasTexture(c);
}

export function buildBullet(): THREE.Mesh {
  const bullet = new THREE.Mesh(
    // One tapered mesh reads as a moving tracer instead of a stationary red
    // dot. It costs the same single projectile draw call as the old sphere;
    // Game aligns its long axis to the shot velocity at launch.
    new THREE.CylinderGeometry(0.07, 0.16, 1.25, 7),
    new THREE.MeshBasicMaterial({ color: '#ff5e4a' }),
  );
  bullet.name = 'projectile-tracer';
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexOrange(),
    color: '#ff6a4a',
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  glow.scale.setScalar(1.4);
  bullet.add(glow);
  return bullet;
}

/* ---------------- shield ---------------- */

export interface ShieldRig {
  group: THREE.Group;
  shell: THREE.Mesh;
  wire: THREE.Mesh;
  rings: THREE.Mesh[];
  glow: THREE.Sprite;
}

/** HODL shield: soft shell, hex-ish wireframe lattice, twin gyro rings, rim glow. */
export function buildShieldBubble(): ShieldRig {
  const group = new THREE.Group();
  const shell = new THREE.Mesh(
    new THREE.SphereGeometry(3.05, 20, 14),
    new THREE.MeshBasicMaterial({ color: '#5ab8ff', transparent: true, opacity: 0.1, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
  );
  const wire = new THREE.Mesh(
    new THREE.IcosahedronGeometry(3.22, 1),
    new THREE.MeshBasicMaterial({ color: '#7dc8ff', transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false, wireframe: true }),
  );
  const ringMat = new THREE.MeshBasicMaterial({ color: '#9adcff', transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false });
  const ringA = new THREE.Mesh(new THREE.TorusGeometry(3.42, 0.05, 6, 40), ringMat);
  ringA.rotation.x = Math.PI / 2;
  const ringB = new THREE.Mesh(new THREE.TorusGeometry(3.42, 0.05, 6, 40), ringMat.clone());
  ringB.rotation.y = Math.PI / 3;
  const glow = makeGlowSprite(9.5);
  glow.material.color.set('#5ab8ff');
  glow.material.opacity = 0.28;
  group.add(shell, wire, ringA, ringB, glow);
  return { group, shell, wire, rings: [ringA, ringB], glow };
}

export interface TruckRig {
  group: THREE.Group;
  wheels: THREE.Mesh[];
  bedY: number;
  cowSlots: THREE.Vector3[]; // local offsets on the flatbed
}

/* ---------------- meteors & ore ---------------- */

export interface MeteorRig {
  group: THREE.Group;
  rock: THREE.Mesh;
  trail: THREE.Sprite;
}

export function buildMeteor(): MeteorRig {
  const group = new THREE.Group();
  const rock = new THREE.Mesh(
    new THREE.DodecahedronGeometry(0.85, 0),
    new THREE.MeshLambertMaterial({ color: '#54402e', emissive: '#c2481a', emissiveIntensity: 0.85, flatShading: true }),
  );
  group.add(rock);
  const trail = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexOrange(),
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  trail.scale.set(2.2, 7, 1);
  trail.position.y = 2.8;
  group.add(trail);
  return { group, rock, trail };
}

export interface OreRig {
  group: THREE.Group;
  core: THREE.Mesh;
  halo: THREE.Sprite;
}

/** Sky ore: what a meteor leaves behind. Beam it up — the mothership pays well. */
export function buildOre(): OreRig {
  const group = new THREE.Group();
  const core = new THREE.Mesh(
    new THREE.DodecahedronGeometry(0.58, 0),
    new THREE.MeshLambertMaterial({ color: '#3c2c1c', emissive: '#f7931a', emissiveIntensity: 0.7, flatShading: true }),
  );
  core.position.y = 0.45;
  group.add(core);
  const halo = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexOrange(),
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  halo.scale.setScalar(2.6);
  halo.position.y = 0.5;
  group.add(halo);
  return { group, core, halo };
}

export interface EmpRig {
  group: THREE.Group;
  rings: THREE.Mesh[];
  shell: THREE.Mesh;
  bolts: THREE.LineSegments;
  glow: THREE.Sprite;
}

/**
 * EMP detonation: three offset plasma fronts, an expanding ion cage and a
 * jagged radial discharge. It is still a tiny, post-processing-free rig for
 * mobile, but reads as a weapon instead of a single expanding doughnut.
 */
export function buildEmpRing(): EmpRig {
  const group = new THREE.Group();
  const ringGeo = new THREE.TorusGeometry(1, 0.055, 6, 64);
  const rings = ['#d8ffff', '#7df9ff', '#629dff'].map((color, i) => {
    const ring = new THREE.Mesh(
      ringGeo,
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.95 - i * 0.16,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    ring.rotation.x = Math.PI / 2 + (i - 1) * 0.08;
    ring.rotation.y = (i - 1) * 0.15;
    ring.scale.setScalar(0.92 + i * 0.1);
    group.add(ring);
    return ring;
  });

  const shell = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.9, 2),
    new THREE.MeshBasicMaterial({
      color: '#92fbff',
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      wireframe: true,
    }),
  );
  shell.scale.y = 0.35;
  group.add(shell);

  const boltPoints: THREE.Vector3[] = [];
  for (let ray = 0; ray < 14; ray++) {
    const a = ray / 14 * Math.PI * 2;
    let previous = new THREE.Vector3(Math.cos(a) * 0.72, (ray % 3 - 1) * 0.018, Math.sin(a) * 0.72);
    for (let segment = 1; segment <= 3; segment++) {
      const r = 0.72 + segment * 0.34;
      const kink = (segment % 2 ? 1 : -1) * (0.025 + (ray % 4) * 0.008);
      const next = new THREE.Vector3(
        Math.cos(a + kink) * r,
        ((ray + segment) % 3 - 1) * 0.025,
        Math.sin(a + kink) * r,
      );
      boltPoints.push(previous, next);
      previous = next;
    }
  }
  const boltGeo = new THREE.BufferGeometry().setFromPoints(boltPoints);
  const bolts = new THREE.LineSegments(
    boltGeo,
    new THREE.LineBasicMaterial({
      color: '#e3ffff',
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  group.add(bolts);

  const glow = makeGlowSprite(3.4);
  glow.material.color.set('#7df9ff');
  glow.material.opacity = 0.8;
  glow.material.rotation = Math.PI / 4;
  group.add(glow);

  return { group, rings, shell, bolts, glow };
}

/* ---------------- poof particles ---------------- */

export interface Poof {
  sprite: THREE.Sprite;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
}

export class PoofPool {
  private pool: THREE.Sprite[] = [];
  readonly active: Poof[] = [];

  constructor(private scene: THREE.Scene) {}

  burst(at: THREE.Vector3, color: 'green' | 'orange' | 'red' | 'blue', count = 10): void {
    for (let i = 0; i < count; i++) {
      const sprite = this.pool.pop() ?? new THREE.Sprite(new THREE.SpriteMaterial({
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }));
      (sprite.material as THREE.SpriteMaterial).map =
        color === 'green' ? glowTexGreen()
          : color === 'red' ? glowTexRed()
            : color === 'blue' ? glowTexBlue()
              : glowTexOrange();
      sprite.material.opacity = 0.85;
      sprite.position.copy(at);
      sprite.scale.setScalar(0.5 + Math.random() * 0.5);
      this.scene.add(sprite);
      const a = Math.random() * Math.PI * 2;
      this.active.push({
        sprite,
        vel: new THREE.Vector3(Math.cos(a) * (1 + Math.random() * 2.5), 1.5 + Math.random() * 3, Math.sin(a) * (1 + Math.random() * 2.5)),
        life: 0,
        maxLife: 0.5 + Math.random() * 0.35,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.life += dt;
      if (p.life >= p.maxLife) {
        this.scene.remove(p.sprite);
        this.pool.push(p.sprite);
        this.active.splice(i, 1);
        continue;
      }
      p.sprite.position.addScaledVector(p.vel, dt);
      p.vel.y -= 4 * dt;
      const t = 1 - p.life / p.maxLife;
      p.sprite.material.opacity = 0.85 * t;
      p.sprite.scale.setScalar(0.5 + (1 - t) * 1.6);
    }
  }
}

/* ---------------- lightning ---------------- */

/** A jagged bolt from `height` down to y=0, with one branch and a glow. */
export function makeLightningBolt(height: number): THREE.Group {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({
    color: '#eaf2ff',
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: false,
  });
  const addPath = (fromY: number, toY: number, spread: number, thickness: number, x0 = 0, z0 = 0) => {
    const segs = 7;
    let px = x0;
    let pz = z0;
    let py = fromY;
    for (let s = 0; s < segs; s++) {
      const ny = fromY + ((toY - fromY) * (s + 1)) / segs;
      const nx = s === segs - 1 ? x0 : px + (Math.random() - 0.5) * spread;
      const nz = s === segs - 1 ? z0 : pz + (Math.random() - 0.5) * spread;
      const dir = new THREE.Vector3(nx - px, ny - py, nz - pz);
      const len = dir.length();
      const seg = new THREE.Mesh(new THREE.CylinderGeometry(thickness, thickness, len, 4), mat);
      seg.position.set((px + nx) / 2, (py + ny) / 2, (pz + nz) / 2);
      seg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
      group.add(seg);
      px = nx; py = ny; pz = nz;
    }
  };
  addPath(height, 0, 2.6, 0.09);
  addPath(height * 0.55, height * 0.18, 3.4, 0.05, (Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2);
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTexBlue(),
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }));
  glow.scale.setScalar(7);
  glow.position.y = 0.6;
  group.add(glow);
  return group;
}

/* ---------------- floating score popups ---------------- */

interface ScorePop {
  sprite: THREE.Sprite;
  life: number;
}

export class ScorePopPool {
  private active: ScorePop[] = [];

  constructor(private scene: THREE.Scene) {}

  spawn(at: THREE.Vector3, text: string, color = '#b4ff8c'): void {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 96;
    const g = c.getContext('2d')!;
    g.font = '700 58px "Chakra Petch", system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineWidth = 8;
    g.strokeStyle = 'rgba(8,9,13,0.9)';
    g.strokeText(text, 128, 50);
    g.shadowColor = color;
    g.shadowBlur = 16;
    g.fillStyle = color;
    g.fillText(text, 128, 50);
    const tex = new THREE.CanvasTexture(c);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
    sprite.scale.set(3.6, 1.35, 1);
    sprite.position.copy(at);
    this.scene.add(sprite);
    this.active.push({ sprite, life: 0 });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.life += dt;
      if (p.life >= 1) {
        this.scene.remove(p.sprite);
        p.sprite.material.map?.dispose();
        p.sprite.material.dispose();
        this.active.splice(i, 1);
        continue;
      }
      p.sprite.position.y += dt * 2.3;
      p.sprite.material.opacity = p.life < 0.7 ? 1 : 1 - (p.life - 0.7) / 0.3;
    }
  }

  /** Reports and the title must never inherit world-space score text. */
  clear(): void {
    for (const pop of this.active) {
      this.scene.remove(pop.sprite);
      pop.sprite.material.map?.dispose();
      pop.sprite.material.dispose();
    }
    this.active.length = 0;
  }
}
